import { decode } from '@msgpack/msgpack';
import { tracePhase } from '../lib/perfProbe';
import { devLog } from '../lib/devLog';
import { scopeKey } from '../lib/userScope';
import { resolveRuntimeWsUrl } from '../lib/convorcherTargets';
import { shouldResumeSession } from '../lib/sessionCharacter';
import { ConversationFlow, clip } from '../lib/conversationFlow';

// Deployed builds connect SAME-ORIGIN (`wss://<page-host>/ws`) — nginx
// forwards to ${CONVORCHER_URL} at runtime, so /api and /ws follow one
// runtime knob and the image bakes no environment (ENG-1009). Dev builds
// derive a direct URL from VITE_CONVORCHER_API_TARGET; an explicit
// VITE_CONVORCHER_WS_URL still wins (ENG-901: the two transports can only
// split deliberately, never by defaults).
export const WS_URL = resolveRuntimeWsUrl(import.meta.env, {
  dev: !!import.meta.env?.DEV,
  locationOrigin: typeof window !== 'undefined' ? window.location.origin : '',
});
// `?api_key=` is a localhost-dev affordance — the avataros_session cookie
// can't reach a local Convorcher (RFC 6265 blocks cross-domain cookie
// reach). Production: env var is unset → no query param → WS auth via the
// cookie attached on the upgrade's Cookie header.
const API_KEY = import.meta.env.VITE_CONVORCHER_API_KEY || '';
const SESSION_KEY = 'convorcher_session_id';
// The character the persisted session was opened under. Stored alongside the id
// because a session is only resumable for its own persona — see
// shouldResumeSession (ENG-907).
const SESSION_CHARACTER_KEY = 'convorcher_session_character';

/**
 * WebSocket client for the Convorcher backend.
 *
 * Connection: wss://…/ws?mode=bsstream&transmission=binary[&session_id=UUID]
 *
 * Inbound messages (binary msgpack OR text JSON):
 *   session          → msg.message = sessionId string; msg.data = { features, stories }
 *   NewResponse      → msg.data = { trigger? }
 *   TextOut          → msg.data = { id, text, sentenceNum, isLast, animations }
 *   StreamChunk      → msg.data = { id, sentenceNum, audio?, frames?, done? }
 *   AudioAnimation   → msg.data = { id, sentenceNum, audio, frames }  (non-streaming fallback)
 *   ResponseComplete → msg.data = { sentenceCount }
 *
 * Outbound (always JSON text):
 *   TextIn  → { type: 'TextIn',  message: 'user text' }
 *   TextOut → { type: 'TextOut', message: 'bot text' }  (echo/TTS mode)
 */
export class ConvorcherClient {
  constructor(handlers = {}) {
    this.handlers = handlers;
    this.ws = null;
    this.connected = false;
    const urlSessionId = new URLSearchParams(window.location.search).get('session_id');
    this.sessionId = urlSessionId || localStorage.getItem(scopeKey(SESSION_KEY)) || null;
    // An explicit ?session_id= is the caller pinning a session on purpose, so
    // seedSessionConfig's character check leaves it alone.
    this._sessionIdFromUrl = !!urlSessionId;
    // Character slug — the server needs this to resolve voice/TTS config.
    // Set via setCharacter() before connect; calling after connect reconnects.
    this.character = new URLSearchParams(window.location.search).get('character') || null;
    // Optional session overrides set by the `convorcherSession` blueprint node
    // (via setSessionConfig). When non-null these win over the page-URL
    // features/story params in connect(). Null = fall back to page params.
    this.featuresOverride = null;
    this.storyOverride = null;
    // TTS fast-model switchover (ENG-945). Null = fall back to the page-URL
    // ?fast_sentences= param, and if that is absent too, to the server default.
    this.fastSentencesOverride = null;

    // Auto-reconnect state. Capped at _maxReconnectAttempts so unreachable
    // servers don't spam the browser's native "WebSocket connection failed"
    // error in the console every 30s forever.
    this._autoReconnect = false;
    this._reconnectAttempt = 0;
    this._reconnectTimer = null;
    this._maxReconnectDelay = 30000;
    this._baseDelay = 1000;
    this._maxReconnectAttempts = 10;
    this._gaveUp = false;

    // Per-response accounting behind the always-on conversation flow log.
    this._flow = new ConversationFlow();
  }

  /** True once startAutoConnect() has been called and stopAutoConnect() hasn't.
   *  Lets App tell "open the first session" apart from "re-push a changed
   *  config on the live one" without duplicating the reconnect. */
  isAutoConnecting() {
    return this._autoReconnect;
  }

  /** Seed the session config BEFORE the first socket, from
   *  sessionCharacter.resolveSessionConfig (ENG-907).
   *
   *  Unlike setSessionConfig this does NOT drop the session id or reconnect:
   *  there is no session to invalidate yet, and clearing the persisted id would
   *  break the "refresh resumes the same session" contract for exactly the
   *  characters whose conversational persona differs from the rendered slug.
   *  Caller must invoke this before startAutoConnect(); it is a no-op guard, not
   *  a second override channel. */
  seedSessionConfig({ character = null, features = null, story = null } = {}) {
    if (this.ws || this._autoReconnect) {
      console.warn('[Convorcher] seedSessionConfig after connect — use setSessionConfig');
      return;
    }
    this.character = character || null;
    this.featuresOverride = features || null;
    this.storyOverride = story || null;
    // A persisted session belongs to the persona it was opened under. Resuming
    // one from a different character makes the server discard it and mint a
    // replacement ("options differ. Starting a new session."), which is the
    // churn this ticket is about — so drop it here instead of on the wire.
    // An explicit ?session_id= in the URL is the caller overriding us; leave it.
    if (this.sessionId && !this._sessionIdFromUrl) {
      const stored = this._readStoredSessionCharacter();
      if (!shouldResumeSession({ storedCharacter: stored, character: this.character })) {
        devLog(
          '[Convorcher] dropping session %s — stored for %s, opening as %s',
          this.sessionId, stored ?? '(unknown)', this.character ?? '(none)',
        );
        this._forgetSession();
      }
    }
  }

  /**
   * One always-on line per response, plus a loud warning when NO AUDIO came
   * back at all — the ENG-907 symptom: convorcher answers with text and TTS
   * drops it ("no voice ID configured, dropping message"), so the character
   * replies mutely. Without this, that was indistinguishable from "still
   * thinking", which is how the bug stayed invisible.
   *
   * The sibling failure — audio arrives but zero ARKit frames — is deliberately
   * NOT warned about here: a2fHealth.js owns it, per delivered sentence, with a
   * consecutive-sentence threshold and a dismissible viewport pill. Warning per
   * response as well would double-report it and fire on a single sentence, which
   * is exactly the false positive that threshold exists to avoid. The raw frame
   * count is still in the summary line below.
   */
  _logResponseSummary(data) {
    const s = this._flow.complete(data);
    const took = s.elapsedMs == null ? '' : ` in ${(s.elapsedMs / 1000).toFixed(1)}s`;
    console.log(
      `[Convorcher] ⇊ response complete${took}: ${s.sentences} sentence(s), ` +
      `${s.audioBytes} audio bytes, ${s.frames} ARKit frames`,
    );
    if (s.silent) {
      console.warn(
        `[Convorcher] ⚠ reply had NO AUDIO as "${this.character ?? '(none)'}" — the character ` +
        'most likely has no voice configured (convorcher: "no voice ID configured, dropping ' +
        `message"). Check with: node tools/convorcher_voice_probe.mjs ${this.character ?? '<character>'}`,
      );
    }
  }

  _readStoredSessionCharacter() {
    try { return localStorage.getItem(scopeKey(SESSION_CHARACTER_KEY)); } catch { return null; }
  }

  /** Drop the session id and its persisted copy. */
  _forgetSession() {
    this.sessionId = null;
    try {
      localStorage.removeItem(scopeKey(SESSION_KEY));
      localStorage.removeItem(scopeKey(SESSION_CHARACTER_KEY));
    } catch { /* ignore */ }
  }

  /** Begin auto-connecting (and reconnecting on close). */
  startAutoConnect() {
    this._autoReconnect = true;
    this._reconnectAttempt = 0;
    this._gaveUp = false;
    this.connect();
  }

  /** Reset the give-up state and try again from scratch. */
  retryConnect() {
    this._reconnectAttempt = 0;
    this._gaveUp = false;
    clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;
    this.connect();
  }

  /** Stop auto-reconnect and disconnect. */
  stopAutoConnect() {
    this._autoReconnect = false;
    clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;
    this.disconnect();
  }

  _scheduleReconnect() {
    if (!this._autoReconnect || this._gaveUp) return;
    if (this._reconnectAttempt >= this._maxReconnectAttempts) {
      this._gaveUp = true;
      console.warn(
        `[Convorcher] Gave up reconnecting after ${this._reconnectAttempt} failed attempts.`,
        '\n  Call window.__CONVORCHER_CLIENT__.retryConnect() to try again.',
      );
      return;
    }
    const delay = Math.min(
      this._baseDelay * Math.pow(2, this._reconnectAttempt),
      this._maxReconnectDelay,
    );
    this._reconnectAttempt++;
    if (this._reconnectAttempt <= 3) {
      console.log(`[Convorcher] Reconnecting in ${delay}ms (attempt ${this._reconnectAttempt})`);
    }
    this._reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  getSessionId() {
    return this.sessionId;
  }

  /** Set the character slug. If already connected, the session is dropped
   *  and reopened so the server resolves voice/TTS config for the new one. */
  setCharacter(slug) {
    if (this.character === slug) return;
    this.character = slug || null;
    // New character → new session; otherwise the server reuses the prior
    // session which was bound to a different (or empty) character.
    this._forgetSession();
    if (this.ws && this._autoReconnect) {
      this.disconnect();
      this._reconnectAttempt = 0;
      this._gaveUp = false;
      this.connect();
    }
  }

  /** Set the full session config from a `convorcherSession` blueprint node:
   *  conversational character slug + optional features/story. Reconnects on a
   *  real change so the server resolves the new persona/voice/feature set. This
   *  lets a blueprint converse as a persona distinct from the rendered
   *  character (e.g. the `isaac-gs` splat runs a graph that sets "isaac"). */
  setSessionConfig({ character = null, features = null, story = null, fastSentences = null } = {}) {
    const ch = character || null;
    const ft = features || null;
    const st = story || null;
    // NOT `fastSentences || null`: 0 is a meaningful value ("never use the fast
    // TTS model") and truthiness would collapse it to "unset", silently handing
    // the session the server default instead. Only null/undefined/'' are unset.
    const fs = fastSentences === null || fastSentences === undefined || fastSentences === ''
      ? null
      : String(fastSentences);
    const changed = this.character !== ch || this.featuresOverride !== ft
      || this.storyOverride !== st || this.fastSentencesOverride !== fs;
    this.character = ch;
    this.featuresOverride = ft;
    this.storyOverride = st;
    // Applied at session creation server-side, so a change has to reopen the
    // session — reusing the socket would keep the previous switchover.
    this.fastSentencesOverride = fs;
    if (!changed) return;
    // New config → new session; the prior one was bound to a different persona.
    this._forgetSession();
    if (this.ws && this._autoReconnect) {
      this.disconnect();
      this._reconnectAttempt = 0;
      this._gaveUp = false;
      this.connect();
    }
  }

  /**
   * Drop the current session and open a brand-new one with the SAME config.
   *
   * The lever the Brain workspace's "Restart session" button (and the manual
   * live-fire specs) need: convorcher snapshots a session's cached-response /
   * prefab sets at creation and marks each as used after one fire, so picking
   * up an edit — or re-firing a once-per-session response — requires a new
   * session object, not a reconnect. setSessionConfig can't express it (same
   * config → `changed` is false → early return), and _forgetSession alone
   * leaves the live socket bound to the old session.
   *
   * Safe while disconnected: the id is forgotten and the next connect starts
   * fresh; no reconnect is forced on a socket that isn't there.
   */
  newSession() {
    this._forgetSession();
    if (this.ws && this._autoReconnect) {
      this.disconnect();
      this._reconnectAttempt = 0;
      this._gaveUp = false;
      this.connect();
    }
  }

  connect() {
    if (this.ws) this.disconnect();

    const params = new URLSearchParams({ mode: 'bsstream', transmission: 'binary' });
    if (this.sessionId) params.set('session_id', this.sessionId);
    if (API_KEY) params.set('api_key', API_KEY);
    // Server resolves voice ID / TTS config by character slug. Without this
    // the session lands with Character: "" and TTS drops every sentence with
    // "no voice ID configured".
    if (this.character) params.set('character', this.character);
    // features/story: a `convorcherSession` node override (setSessionConfig)
    // wins over the page-URL params (e.g. /?features=storyteller&story=myra_v4),
    // so the graph is the single source of truth when it declares a session.
    const pageParams = new URLSearchParams(window.location.search);
    const features = this.featuresOverride ?? pageParams.get('features');
    const story    = this.storyOverride ?? pageParams.get('story');
    if (features) params.set('features', features);
    if (story) params.set('story', story);
    // TTS fast-model switchover (ENG-945). Omitted when unset so the server
    // applies its own default; '0' must still be sent, hence the null check
    // rather than a truthiness test.
    const fastSentences = this.fastSentencesOverride ?? pageParams.get('fast_sentences');
    if (fastSentences !== null && fastSentences !== '') params.set('fast_sentences', fastSentences);
    const url = `${WS_URL}?${params}`;

    if (this._reconnectAttempt <= 1) {
      console.log('[Convorcher] Connecting to', url);
    }
    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this.connected = true;
      this._reconnectAttempt = 0;
      console.log('[Convorcher] Connected');
      this.handlers.onConnect?.();
    };

    this.ws.onmessage = (event) => this._handleMessage(event.data);

    this.ws.onclose = (event) => {
      const wasConnected = this.connected;
      this.connected = false;
      if (wasConnected) {
        console.log('[Convorcher] Disconnected', event.code, event.reason);
      }
      this.handlers.onDisconnect?.(event.code, event.reason);
      this._scheduleReconnect();
    };

    this.ws.onerror = () => {
      if (this._reconnectAttempt <= 1) {
        console.warn('[Convorcher] WebSocket error');
      }
      this.handlers.onError?.();
    };
  }

  disconnect() {
    clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;
    if (this.ws) {
      // Detach handlers before closing to prevent stale onclose from
      // triggering reconnect after a new connection has already started.
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
      this.connected = false;
    }
  }

  /** Send user text to the LLM. Always sent as JSON text, not msgpack. */
  sendTextIn(text) {
    devLog('[Convorcher] sendTextIn() called with:', JSON.stringify(text), '| connected:', this.connected, '| ws.readyState:', this.ws?.readyState)
    if (!this.connected || !this.ws) {
      console.warn(
        `[Convorcher] ✗ NOT SENT (not connected) as "${this.character ?? '(none)'}": "${clip(text)}"`,
      );
      return;
    }
    this.ws.send(JSON.stringify({ type: 'TextIn', message: text }));
    // Always-on: the sent prompt is the anchor every later flow line hangs off.
    console.log(`[Convorcher] ↑ sent as "${this.character}": "${clip(text)}"`);
    this._flow.sent(text);
  }

  /** Send a raw DEBUG command string (e.g. "trigger:greeting"). */
  sendDebugCommand(command) {
    if (!this.connected || !this.ws) {
      console.warn('[Convorcher] Cannot send DEBUG — not connected');
      return false;
    }
    this.ws.send(JSON.stringify({ type: 'DEBUG', message: command }));
    devLog('[Convorcher] Sent DEBUG:', command);
    return true;
  }

  /** Send a prefab trigger debug command: "trigger:<name>". */
  sendDebugCommandTrigger(prefabName) {
    if (!prefabName) return false;
    return this.sendDebugCommand(`trigger:${prefabName}`);
  }

  _handleMessage(data) {
    let msg;

    if (data instanceof ArrayBuffer) {
      if (data.byteLength === 0) return; // ignore empty frames

      if (data instanceof Blob) {
        // Blob path (shouldn't happen since binaryType = 'arraybuffer', but just in case)
        const reader = new FileReader();
        reader.onload = () => {
          try {
            this._dispatch(decode(new Uint8Array(reader.result)));
          } catch (e) {
            console.error('[Convorcher] Failed to decode Blob msgpack', e);
          }
        };
        reader.readAsArrayBuffer(data);
        return;
      }

      try {
        msg = tracePhase(`msgpack.decode.${data.byteLength}B`, () =>
          decode(new Uint8Array(data))
        );
      } catch (e) {
        console.error('[Convorcher] Failed to decode binary msgpack', e);
        return;
      }
    } else if (typeof data === 'string') {
      try {
        msg = JSON.parse(data);
      } catch (e) {
        console.error('[Convorcher] Failed to parse JSON message', e);
        return;
      }
    } else {
      console.warn('[Convorcher] Unknown message data type', typeof data);
      return;
    }

    const type = msg.type || msg.Type || 'unknown';
    tracePhase(`dispatch.${type}`, () => this._dispatch(msg));
  }

  _dispatch(msg) {
    const type = msg.type || msg.Type;

    // Debug: log every decoded message inline so the type + payload sizes
    // are visible without expanding objects in the console.
    try {
      const d = msg.data ?? {};
      const parts = [`type=${type}`];
      if (d.sentenceNum !== undefined) parts.push(`n=${d.sentenceNum}`);
      if (d.isLast !== undefined)      parts.push(`isLast=${d.isLast}`);
      if (d.done !== undefined)        parts.push(`done=${d.done}`);
      const audioLen = d.audio?.length ?? d.audio?.byteLength;
      if (audioLen !== undefined)      parts.push(`audio=${audioLen}B`);
      if (Array.isArray(d.frames))     parts.push(`frames=${d.frames.length}`);
      if (typeof d.text === 'string')  parts.push(`text="${d.text.slice(0, 60)}"`);
      if (d.trigger)                   parts.push(`trigger=${d.trigger}`);
      if (d && typeof d === 'object' && !audioLen && !d.frames && !d.text) {
        parts.push(`keys=[${Object.keys(d).join(',')}]`);
      }
      devLog('[Convorcher RX]', parts.join('  '));
    } catch (e) { /* ignore */ }

    switch (type) {
      case 'session': {
        // Session ID is in msg.message (the WsMessage.Message field)
        const id = msg.message || null;
        if (id) {
          this.sessionId = id;
          try {
            localStorage.setItem(scopeKey(SESSION_KEY), id);
            // Persist WHICH character this session belongs to, so the next load
            // only resumes it when conversing as the same persona (ENG-907).
            if (this.character) localStorage.setItem(scopeKey(SESSION_CHARACTER_KEY), this.character);
            else localStorage.removeItem(scopeKey(SESSION_CHARACTER_KEY));
          } catch { /* ignore */ }
        }
        console.log('[Convorcher] Session ID:', this.sessionId);
        this.handlers.onSession?.(this.sessionId, msg.data);
        break;
      }
      case 'NewResponse': {
        const trigger = this._flow.acknowledged(msg.data);
        console.log(`[Convorcher] ✓ acknowledged — generating${trigger ? ` (trigger=${trigger})` : ''}`);
        this.handlers.onNewResponse?.(msg.data ?? msg);
        break;
      }
      case 'TextOut': {
        const d = msg.data ?? {};
        this._flow.textOut(d);
        console.log(`[Convorcher] ↓ s${d.sentenceNum ?? 0}${d.isLast ? ' (last)' : ''}: "${clip(d.text)}"`);
        this.handlers.onTextOut?.(msg.data ?? msg);
        break;
      }
      case 'StreamChunk': {
        const chunk = msg.data ?? msg;
        this._flow.audio({
          sentenceNum: chunk.sentenceNum,
          audioBytes: chunk.audio?.length ?? chunk.audio?.byteLength ?? 0,
          frames: Array.isArray(chunk.frames) ? chunk.frames.length : 0,
        });
        this.handlers.onStreamChunk?.(chunk);
        break;
      }
      case 'AudioAnimation':
        // Non-streaming fallback (mode=blendshapes)
        this.handlers.onAudioAnimation?.(msg.data ?? msg);
        break;
      case 'ResponseComplete': {
        this._logResponseSummary(msg.data);
        this.handlers.onResponseComplete?.(msg.data ?? msg);
        break;
      }
      case 'Metrics':
        devLog('[Convorcher] Metrics:', msg.data ?? msg);
        this.handlers.onMetrics?.(msg.data ?? msg);
        break;
      case 'Error':
        console.warn('[Convorcher] Server error:', msg.message, msg.data);
        this.handlers.onServerError?.({ message: msg.message ?? null, data: msg.data ?? null });
        break;
      default:
        devLog('[Convorcher] Unhandled message type:', type);
    }
  }
}
